/**
 * Wave-J — Reusable table pagination.
 *
 * Default: 10 rows per page, with a 10/25/50/100 size selector.
 * Use the `usePager` hook from a list page:
 *
 *   const { visibleRows, controls } = usePager(filteredRows, 10);
 *   <tbody>{visibleRows.map(...)}</tbody>
 *   {controls}
 *
 * The component is unstyled-by-default to fit any page.
 */
import React, { useMemo, useState, useEffect } from "react";

export function usePager(rows, defaultSize = 10) {
  const [pageSize, setPageSize] = useState(defaultSize);
  const [page, setPage] = useState(1);

  const total = rows?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // Snap back to page 1 whenever the underlying rows or size changes.
  useEffect(() => { setPage(1); }, [total, pageSize]);
  // Guard: if rows shrink below our current page, snap back.
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const visibleRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (rows || []).slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  const controls = total <= defaultSize ? null : (
    <Pagination
      page={page} pageSize={pageSize} total={total}
      onPage={setPage} onPageSize={setPageSize}
    />
  );

  return { visibleRows, controls, page, setPage, pageSize, setPageSize };
}


export default function Pagination({ page, pageSize, total, onPage, onPageSize }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  // Build the page-number window: always show 1, last, current ± 1
  const window = useMemo(() => {
    const set = new Set([1, totalPages, page - 1, page, page + 1]);
    return [...set].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  }, [page, totalPages]);

  return (
    <div className="flex items-center justify-between gap-4 px-4 py-2 border-t border-[#EEEEEE] bg-[#F5F5F5]/50 text-[11px] font-mono text-[#0D2B5E]/70" data-testid="pagination">
      <div>
        Showing <b>{start}</b>–<b>{end}</b> of <b>{total}</b>
      </div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2">
          <span className="text-[10px] tracking-widest uppercase">Rows</span>
          <select value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))} data-testid="page-size-select"
            className="border border-[#EEEEEE] bg-white px-1.5 py-0.5 rounded-sm text-[11px]">
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
        <div className="flex items-center gap-0.5">
          <button onClick={() => onPage(Math.max(1, page - 1))} disabled={page <= 1} data-testid="page-prev"
            className="px-2 py-0.5 border border-[#EEEEEE] bg-white rounded-sm disabled:opacity-30">‹</button>
          {window.map((p, i) => {
            const prev = window[i - 1];
            return (
              <React.Fragment key={p}>
                {prev && p - prev > 1 && <span className="px-1 text-[#0D2B5E]/40">…</span>}
                <button onClick={() => onPage(p)} data-testid={`page-${p}`}
                  className={`px-2 py-0.5 border rounded-sm ${p === page ? "bg-[#1565C0] text-white border-[#1565C0] font-bold" : "border-[#EEEEEE] bg-white"}`}>
                  {p}
                </button>
              </React.Fragment>
            );
          })}
          <button onClick={() => onPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} data-testid="page-next"
            className="px-2 py-0.5 border border-[#EEEEEE] bg-white rounded-sm disabled:opacity-30">›</button>
        </div>
      </div>
    </div>
  );
}
